import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import SGTitle from 'components/Typography/SGTitle';
import SGBody from 'components/Typography/SGBody';
import Device from 'features/POSRegisters/interfaces/Device';
import {PaymentTerminalScanDetails} from 'features/POSRegisters/interfaces/ScanDetails';
import Alert from 'features/POSRegisters/components/Alert';
import getSubTypeAlerts from 'features/POSRegisters/functions/getSubTypeAlerts';
import {AlertScanSubType} from 'features/POSRegisters/interfaces/AlertInfo';

//#endregion styles
const StyledContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 8px;
  gap: 16px;
`;

const StyledGrid = styled(Grid)`
  display: grid;
  grid-template-columns: 10rem auto 1fr;
  gap: 16px;
  align-items: center;
  color: ${props => props.theme.text};
  background: ${props => props.theme.disabledStateBlue}33;
  overflow-x: auto;
`;

const StyledRightAlignedText = styled(SGTitle)`
  text-align: right;
`;

const StyledDivider = styled.div`
  height: 100%;
  border-left: 1px solid ${props => props.theme.grey};
`;

const StyledDetails = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 8px 0;
  padding-right: 8px;
`;

const StyledDetailsRow = styled(Grid)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, auto));
  grid-template-rows: auto;
  grid-auto-flow: row;
  gap: 16px;
  justify-content: start;
`;

const StyledDetail = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  margin: 0;
  text-align: left;
  gap: 4px;
`;
const StyledLabel = styled(SGTitle)`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${props => props.theme.disabledStateBlue};
`;

//#endregion styles

export default function PaymentTerminalSummary(
  paymentTerminal: Device<PaymentTerminalScanDetails>
) {
  const {
    pingStatus,
    ipAddress,
    macAddress,
    vlanNumber,
    model,
    manufacturer,
    serialNumber,
    switchId,
    switchNumber,
    portNumber,
    deviceName,
  } = paymentTerminal.scanResults ?? {};

  return (
    <StyledContainer>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">CONNECTIVITY</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">PING STATUS</StyledLabel>
              <SGBody className="b2">{pingStatus ?? 'Failure'}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">IP ADDRESS</StyledLabel>
              <SGBody className="b2">{ipAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MAC ADDRESS</StyledLabel>
              <SGBody className="b2">{macAddress}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Vlan</StyledLabel>
              <SGBody className="b2">{vlanNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Switch ID</StyledLabel>
              <SGBody className="b2">{switchId}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Switch Number</StyledLabel>
              <SGBody className="b2">{switchNumber}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">Port Number</StyledLabel>
              <SGBody className="b2">{portNumber}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(paymentTerminal.alerts, AlertScanSubType.CONNECTIVITY).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={paymentTerminal}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
        </StyledDetails>
      </StyledGrid>
      <StyledGrid>
        <StyledRightAlignedText className="text-color t5">INFORMATION</StyledRightAlignedText>
        <StyledDivider />
        <StyledDetails>
          <SGTitle className="text-color t5">HARDWARE</SGTitle>
          <StyledDetailsRow>
            <StyledDetail>
              <StyledLabel className="t6">DEVICE NAME</StyledLabel>
              <SGBody className="b2">{deviceName}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MANUFACTURER</StyledLabel>
              <SGBody className="b2">{manufacturer}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">MODEL</StyledLabel>
              <SGBody className="b2">{model}</SGBody>
            </StyledDetail>
            <StyledDetail>
              <StyledLabel className="t6">SERIAL NUMBER</StyledLabel>
              <SGBody className="b2">{serialNumber}</SGBody>
            </StyledDetail>
          </StyledDetailsRow>

          {getSubTypeAlerts(paymentTerminal.alerts, AlertScanSubType.HARDWARE).map(
            (alert, alertIdx) => (
              <Alert
                key={`${deviceName}-${alertIdx}`}
                device={paymentTerminal}
                alert={alert}
                rows={[
                  {label: 'Issue', value: alert.description, fullWidth: true},
                  {label: 'Effect', value: alert.effect, fullWidth: true},
                  {label: 'Solution', value: `${alert.solution} ${alert.kb}`, fullWidth: true},
                ]}
              />
            )
          )}
        </StyledDetails>
      </StyledGrid>
    </StyledContainer>
  );
}
